import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import { memo, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectOptionChainVisibleStrikes } from '../../../selectors/common'
import { strikeValues } from '../../../util/tradePage'

const CENTER_COL_WIDTH = 100
const HEADER_ROW_HEIGHT = 30
const DATA_ROW_HEIGHT = 32
const TEXT_BLOCK_HEIGHT = 14

const sidePanelSx = {
    flex: `0 0 calc((100% - ${CENTER_COL_WIDTH}px) / 2)`,
    maxWidth: `calc((100% - ${CENTER_COL_WIDTH}px) / 2)`,
    minWidth: 0
} as const

const centerPanelSx = {
    flex: `0 0 ${CENTER_COL_WIDTH}px`,
    maxWidth: `${CENTER_COL_WIDTH}px`,
    width: `${CENTER_COL_WIDTH}px`
} as const

const tableCellBaseSx = {
    py: 0,
    px: '4px',
    borderBottom: 1,
    borderColor: 'divider',
    lineHeight: 1
} as const

/**
 * Structure-only placeholder for shimmer-from-structure.
 * Shimmer measures these elements and overlays its effect - no MUI Skeleton needed.
 */
function OptionChainSkeleton() {
    const visibleStrikes = useSelector(selectOptionChainVisibleStrikes)
    const rowCount = useMemo(() => {
        if (visibleStrikes === 'all') {
            return strikeValues.maxValue
        }
        const parsed = Number(visibleStrikes)
        return Number.isFinite(parsed) && parsed > 0 ? parsed : Number(strikeValues.values[1]?.value ?? 15)
    }, [visibleStrikes])

    const renderSideHeaderCells = (count: number) => {
        const cells = []
        for (let i = 0; i < count; i++) {
            cells.push(
                <TableCell
                    key={i}
                    sx={{
                        ...tableCellBaseSx,
                        height: HEADER_ROW_HEIGHT,
                        textAlign: 'right'
                    }}
                >
                    <Box sx={{ height: TEXT_BLOCK_HEIGHT, width: i === 0 ? 72 : 56, ml: 'auto' }} />
                </TableCell>
            )
        }
        return cells
    }

    const renderSideDataCells = (colCount: number) =>
        [...Array(colCount)].map((_, j) => (
            <TableCell
                key={j}
                sx={{
                    ...tableCellBaseSx,
                    height: DATA_ROW_HEIGHT,
                    textAlign: 'right'
                }}
            >
                <Box sx={{ height: TEXT_BLOCK_HEIGHT, width: j % 2 === 0 ? 64 : 48, ml: 'auto' }} />
            </TableCell>
        ))

    const renderCenterHeaderCell = () => (
        <TableCell
            sx={{
                ...tableCellBaseSx,
                height: HEADER_ROW_HEIGHT,
                width: CENTER_COL_WIDTH,
                maxWidth: CENTER_COL_WIDTH,
                minWidth: CENTER_COL_WIDTH,
                textAlign: 'center',
                px: 0
            }}
        >
            <Box sx={{ height: TEXT_BLOCK_HEIGHT, width: 48, mx: 'auto' }} />
        </TableCell>
    )

    const renderCenterDataCell = (rowIndex: number) => (
        <TableCell
            sx={{
                ...tableCellBaseSx,
                height: DATA_ROW_HEIGHT,
                width: CENTER_COL_WIDTH,
                maxWidth: CENTER_COL_WIDTH,
                minWidth: CENTER_COL_WIDTH,
                textAlign: 'center',
                px: 0
            }}
        >
            <Box sx={{ height: TEXT_BLOCK_HEIGHT, width: rowIndex % 3 === 0 ? 44 : 40, mx: 'auto' }} />
        </TableCell>
    )

    return (
        <Paper
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                borderRadius: 0,
                overflow: 'hidden'
            }}
        >
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0, width: '100%' }}>
                <Box sx={{ ...sidePanelSx, borderRight: 1, borderColor: 'divider' }}>
                    <TableContainer sx={{ height: '100%' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ height: HEADER_ROW_HEIGHT }}>{renderSideHeaderCells(4)}</TableRow>
                            </TableHead>
                            <TableBody>
                                {[...Array(rowCount)].map((_, i) => (
                                    <TableRow key={i} sx={{ height: DATA_ROW_HEIGHT }}>
                                        {renderSideDataCells(4)}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box
                    sx={{
                        ...centerPanelSx,
                        borderRight: 1,
                        borderColor: 'divider',
                        borderLeft: 1
                    }}
                >
                    <TableContainer sx={{ height: '100%', width: CENTER_COL_WIDTH, maxWidth: CENTER_COL_WIDTH }}>
                        <Table size="small" stickyHeader sx={{ width: CENTER_COL_WIDTH, tableLayout: 'fixed' }}>
                            <TableHead>
                                <TableRow sx={{ height: HEADER_ROW_HEIGHT }}>{renderCenterHeaderCell()}</TableRow>
                            </TableHead>
                            <TableBody>
                                {[...Array(rowCount)].map((_, i) => (
                                    <TableRow key={i} sx={{ height: DATA_ROW_HEIGHT }}>
                                        {renderCenterDataCell(i)}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <Box sx={sidePanelSx}>
                    <TableContainer sx={{ height: '100%' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow sx={{ height: HEADER_ROW_HEIGHT }}>{renderSideHeaderCells(4)}</TableRow>
                            </TableHead>
                            <TableBody>
                                {[...Array(rowCount)].map((_, i) => (
                                    <TableRow key={i} sx={{ height: DATA_ROW_HEIGHT }}>
                                        {renderSideDataCells(4)}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>
        </Paper>
    )
}

export default memo(OptionChainSkeleton)
